import { useEffect } from 'react';

import events from '~/modules/game-events/game-events';
import { useEventListenerSelector } from '~/modules/game-events/hooks';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { nextIndex, nextValue } from '~/modules/utils/indexes';
import tuple from '~/modules/utils/tuple';
import InputSources from '~/routes/select-input/input-sources/index';
import { InputSourceList, InputSourceNames } from '~/routes/select-input/input-sources/interfaces';

export function usePlayerInput(playerNumber: PlayerNumber, sources: Record<string, InputSourceList>) {
  const sourceList = Object.keys(sources) as Array<InputSourceNames>;
  const [selectedPlayerInput, playerInputData, isPlayerInputInitialised] = useEventListenerSelector(
    [events.playerInputChanged, events.inputListChanged],
    () => {
      const playerInput = PlayersManager.getPlayer(playerNumber)?.input;

      if (!playerInput) return tuple([null, null, true]);
      return tuple([
        playerInput,
        InputSources.getInputForPlayerSelected(playerInput),
        InputSources.isPlayerInputInitialised(playerInput),
      ]);
    },
  );

  useEffect(() => {
    if (isPlayerInputInitialised && playerInputData === null) {
      const source = sourceList[0];
      const input = sources[source].getDefault();
      if (input) {
        PlayersManager.getPlayer(playerNumber)?.changeInput(sourceList[0], input.channel, input.deviceId);
      }
    }
  });

  const cycleSource = (startingIndex?: number) => {
    const nextSource = nextValue(sourceList, selectedPlayerInput?.source ?? sourceList[0]);

    const input =
      startingIndex !== undefined ? sources[nextSource].list[startingIndex] : sources[nextSource].getDefault();

    PlayersManager.getPlayer(playerNumber)?.changeInput(nextSource, input?.channel, input?.deviceId);
  };
  const cycleInput = () => {
    if (!selectedPlayerInput) return;
    const list = sources[selectedPlayerInput.source].list;
    const currentIndex = playerInputData ? list.findIndex((item) => item.id === playerInputData.id) : 0;
    const newIndex = nextIndex(list, currentIndex);
    if (newIndex === 0) {
      cycleSource(0);
    } else {
      const input = list[newIndex];

      PlayersManager.getPlayer(playerNumber)?.changeInput(selectedPlayerInput.source, input?.channel, input?.deviceId);
    }
  };

  return tuple([selectedPlayerInput?.source ?? null, cycleSource, playerInputData, cycleInput]);
}
